import { useEffect, useRef, useState } from 'react';
import { IonSpinner, useIonRouter } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { OrderCard } from '../orders/OrderCard';
import { VerificationBubbles } from '../orders/VerificationBubbles';
import { OnMyWayButton } from '../orders/OnMyWayButton';
import { DishInfoCard } from '../listings/DishInfoCard';
import { MarkReadButton } from './MarkReadButton';
import { SwipeToRead } from './SwipeToRead';
import { useListingOrders, useOrderDetail, useVerifyOrder } from '../../hooks/useOrders';
import { useMarkActivitiesRead } from '../../hooks/useActivities';
import { useProfileStore } from '../../store/useProfileStore';
import type { ListingOrderSummary } from '../../services/orderService';

interface ActivityOrderCardProps {
  orderId: string;
  activityId: string;
  onActivityRead?: () => void;
  /** Manual dismissal (the mark-read button, or a swipe). Handed up to the
   *  parent, which owns the mark-read call, the list reload and the undo
   *  toast — so a dismissal here is as reversible as one anywhere else. */
  onDismiss?: (activityId: string) => void;
  onReviewed?: () => void;
  onNavigate?: () => void;
  /** Render a compact DishInfoCard above the order — used by the orderer's activity feed. */
  showListingHeader?: boolean;
  /** When true, scroll this card into view once its order data has loaded
   * (used when the sheet is opened from an order activity toast). */
  autoScroll?: boolean;
  /** Called after the auto-scroll fires so the target can be cleared. */
  onAutoScrolled?: () => void;
}

export const ActivityOrderCard: React.FC<ActivityOrderCardProps> = ({ orderId, activityId, onActivityRead, onDismiss, onReviewed, onNavigate, showListingHeader, autoScroll, onAutoScrolled }) => {
  const { t } = useTranslation('activity');
  const router = useIonRouter();
  // A deleted order 404s: the card renders nothing (below), so no error modal.
  const { data, isLoading } = useOrderDetail(orderId, { quiet404: true });
  // Chef's accepted drop-off: the run button needs to know whether other
  // drop-off orders on the listing still need a decision, or it sends them to
  // a locked run. Only fetched for that card; shared cache key per listing.
  const needsRunState =
    !!data && !!data.orderedBy && data.fulfillmentMethod === 'dropoff' && data.status === 'accepted';
  const { data: listingOrders } = useListingOrders(needsRunState ? data?.listing?.id : undefined);
  const dropoffRunPendingCount = needsRunState
    ? (listingOrders ?? []).filter(
        (o) => o.fulfillmentMethod === 'dropoff' && (o.status === 'paid' || o.status === 'received'),
      ).length
    : 0;
  const verifyMutation = useVerifyOrder();
  const markRead = useMarkActivitiesRead();
  const [verifyError, setVerifyError] = useState('');
  const markedRef = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const scrolledRef = useRef(false);

  // Scroll into view once the order data has loaded and the card has rendered.
  // Guarded so it fires at most once even as the card re-renders.
  useEffect(() => {
    if (!autoScroll || scrolledRef.current || !data || !rootRef.current) return;
    scrolledRef.current = true;
    rootRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    onAutoScrolled?.();
  }, [autoScroll, data, onAutoScrolled]);

  // Auto-mark read once the user has reviewed a completed order. Canceled
  // orders stay in Unread until the user dismisses them via the X button so
  // they actually see the cancellation happened.
  useEffect(() => {
    if (!data || markedRef.current) return;
    if (data.hasReviewed) {
      markedRef.current = true;
      const profileId = useProfileStore.getState().activeProfile?.id;
      if (profileId) {
        markRead.mutate(
          { profileId, activityIds: [activityId] },
          { onSuccess: () => onActivityRead?.() },
        );
      }
    }
    // markRead is a stable mutation object from TanStack; excluded so a
    // re-render can't re-arm this effect (same reason as ActivityListingStatusCard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activityId, onActivityRead]);

  // No undo toast on the auto-read above: it's a consequence of the user
  // reviewing the order, not a dismissal they could have meant differently.
  const handleDismiss = () => onDismiss?.(activityId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <IonSpinner name="crescent" className="h-5 w-5" />
      </div>
    );
  }

  if (!data) return null;

  const order: ListingOrderSummary = {
    orderId: data.orderId,
    orderNumber: data.orderNumber,
    status: data.status,
    quantity: data.quantity,
    fulfillmentMethod: data.fulfillmentMethod,
    // Gross + per-unit price: the lister's card shows what they're made whole
    // on, not the eater's fee-and-tax-inclusive total. pricePerUnit rides along
    // so the card can still derive gross if the API hasn't sent `subtotal`.
    subtotal: data.subtotal,
    pricePerUnit: data.pricePerUnit,
    totalPaid: data.totalPaid,
    createdAt: data.createdAt,
    intendedPickupTime: data.intendedPickupTime,
    dropoffFee: data.dropoffFee,
    // Drop-off: the window the run is due in, and (lister side, once accepted)
    // where to drive. Same fields GET /listings/{id}/orders carries.
    dropoffWindow: data.listing?.dropoffWindow,
    orderAddress: data.location?.deliveryAddress,
    listingTimeWindowEnd: data.listingTimeWindowEnd,
    verificationCode: data.verification?.code,
    // On-my-way state — carried onto the summary so the lister card can show the
    // "customer is on their way" indicator (the orderer feed gates its own UI
    // off data.onMyWay directly, below).
    onMyWay: data.onMyWay,
    // Completion-handshake state — without these the lister's confirm card is
    // stuck on "orderer hasn't confirmed" (both read as undefined). The order
    // detail endpoint returns them at handoff; carry them onto the summary.
    ordererConfirmed: data.ordererConfirmed,
    listerConfirmed: data.listerConfirmed,
    // Nudge budget — same reason: without it the Remind button reads as unspent
    // on this surface even after the lister has already sent their one prod.
    listerNudged: data.listerNudged,
    hasReviewed: data.hasReviewed,
    reviewEligible: data.reviewEligible,
    orderedFor: data.orderedFor
      ? { id: data.orderedFor.id, displayName: data.orderedFor.displayName ?? data.orderedFor.businessName ?? '', avatarUrl: data.orderedFor.avatarUrl ?? '', connectionLevel: data.orderedFor.connectionLevel }
      : undefined,
    lister: data.lister
      ? { id: data.lister.id, displayName: data.lister.displayName ?? data.lister.businessName ?? '', avatarUrl: data.lister.avatarUrl ?? '', connectionLevel: data.lister.connectionLevel }
      : undefined,
    amountTotal: data.amountTotal,
    canceledBy: data.canceledBy,
    amountCaptured: data.amountCaptured,
    amountRefunded: data.amountRefunded,
    amountFees: data.amountFees,
  };

  const showDismiss =
    data.status === 'canceled' ||
    data.status === 'expired' ||
    data.status === 'no_show' ||
    (data.status === 'completed' && !data.hasReviewed);
  const isOrderer = !data.orderedBy;

  const handleNavigate = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    onNavigate?.();
    router.push(`/orders/${orderId}`, 'forward');
  };

  const handleVerify = (code: number) => {
    setVerifyError('');
    verifyMutation.mutate(
      { id: orderId, code },
      {
        onSuccess: (result) => {
          if (!result.success) {
            setVerifyError(t('verify.wrongCode'));
          }
        },
      },
    );
  };

  // Pickup handoff: the code is ungated, so the bubbles stand on their own and
  // "On My Way" rides above them as an optional heads-up to the chef rather than
  // as the thing that reveals the number. It drops once the chef has pressed
  // Handed Off — no lead time left to signal. Mirrors OrderInProgressPage.
  const showOnMyWay =
    isOrderer &&
    data.fulfillmentMethod !== 'dropoff' &&
    data.status === 'ready_to_pickup' &&
    !data.onMyWay &&
    !data.listerConfirmed;
  const showVerification = isOrderer && data.verification?.code != null;

  return (
    // `showDismiss` gates both affordances together: an order still awaiting
    // action has no mark-read button, and must not be swipeable either.
    <SwipeToRead
      onCommit={handleDismiss}
      label={t('card.markAsRead')}
      disabled={!showDismiss}
      radius="xl"
    >
    <div
      ref={rootRef}
      className={`relative ${isOrderer ? 'cursor-pointer' : ''}`}
      onClick={isOrderer ? handleNavigate : undefined}
    >
      <OrderCard
        order={order}
        activityId={activityId}
        pickupTimezone={data.listing?.timeWindow?.timezone}
        dropoffRunPendingCount={dropoffRunPendingCount}
        // `?run=1` lands the chef on the listing's drop-off run panel, where
        // the "I'm leaving now" button is.
        onOpenDropoffRun={
          data.listing?.id
            ? () => {
                onNavigate?.();
                router.push(`/listing/${data.listing!.id}?run=1`, 'forward');
              }
            : undefined
        }
        onReviewed={() => {
          onActivityRead?.();
          onReviewed?.();
        }}
        topRightActions={
          showDismiss ? (
            <MarkReadButton onClick={handleDismiss} label={t('card.markAsRead')} />
          ) : undefined
        }
        listingHeader={
          showListingHeader && data.listing ? (
            <DishInfoCard
              listing={{
                title: data.listing.title,
                imageUrl: data.listing.imageUrl,
                timeWindow: data.listing.timeWindow,
                // The order's own hand-off window (the API sends only the one
                // for this order's method), not the envelope over both.
                pickupWindow: data.listing.pickupWindow ?? null,
                dropoffWindow: data.listing.dropoffWindow ?? null,
                fulfillmentMethod: data.fulfillmentMethod ? [data.fulfillmentMethod] : undefined,
                listerSummary: data.lister
                  ? {
                      id: data.lister.id,
                      displayName: data.lister.displayName ?? data.lister.businessName,
                      avatarUrl: data.lister.avatarUrl,
                      connectionLevel: data.lister.connectionLevel,
                    }
                  : undefined,
              }}
              disableExpand
            />
          ) : undefined
        }
        verificationSlot={
          showOnMyWay || showVerification ? (
            <div className="space-y-2">
              {showOnMyWay && (
                <div className="rounded-lg bg-muted/40 p-4">
                  <OnMyWayButton orderId={orderId} />
                </div>
              )}
              {showVerification && (
                <div className="rounded-lg bg-muted/40 p-4 space-y-2">
                  <VerificationBubbles
                    code={data.verification!.code!}
                    onSelect={handleVerify}
                    isSubmitting={verifyMutation.isPending}
                    lister={data.lister}
                    confirmed={!!data.ordererConfirmed}
                    handedOff={!!data.listerConfirmed}
                  />
                  {verifyError && (
                    <p className="text-center text-sm text-brand-danger">{verifyError}</p>
                  )}
                </div>
              )}
            </div>
          ) : undefined
        }
      />
    </div>
    </SwipeToRead>
  );
};
